<template>
  <div
    class="w-full min-h-0 flex flex-col pt-[calc(env(safe-area-inset-top)+62px)] px-4 pb-0! tablet-h:mt-[18px] tablet-h:pt-0 tablet-h:pl-10 tablet-h:pr-[26px]"
    @click="handleClickOutside"
  >
    <AlertBanner class="mb-[15px] tablet-h:mb-[18px]" />

    <div class="mb-[15px] tablet-h:mb-4">
      <h1
        class="tablet-h:hidden text-[24px] font-medium leading-[30px] text-gray-800"
      >
        Archived registrations
      </h1>
      <h1
        class="hidden tablet-h:block text-[22px] leading-[26px] tracking-[-0.25px] font-medium text-gray-800"
      >
        Archived registrations
      </h1>
    </div>

    <ArchiveTable
      ref="tableRef"
      class="-mr-4 tablet-h:mr-0"
      :items="items"
      :loading="loading"
      v-model:selected="selected"
      v-model:sort="sort"
      @scroll-end="fetchNextPage"
    />
  </div>

  <ArchiveSidebar
    :selected-item="selectedItem"
    :total="result?.archivedFiles.total"
    class="hidden tablet-h:flex h-[calc(100vh-61px)]! tablet-h:absolute top-0 right-0 bottom-0 desktop:static"
  />
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { type TableSort } from '@dacr/library'
import {
  SearchSort,
  useArchivedFilesQuery,
  type ArchivedFile,
} from '@/shared/graphql/legacy/generated'
import ArchiveTable from '@/features/explorer/components/ArchiveTable.vue'
import ArchiveSidebar from '@/features/explorer/components/ArchiveSidebar.vue'
import AlertBanner from '@/shared/components/AlertBanner.vue'

const tableRef = ref<InstanceType<typeof ArchiveTable>>()

const selected = ref<string[]>([])
const sort = ref<TableSort | null>({ field: 'name', direction: 'asc' })
const sortBy = ref<SearchSort>(SearchSort.NameAsc)

const SORT_MAP: Record<string, SearchSort> = {
  'name:asc': SearchSort.NameAsc,
  'name:desc': SearchSort.NameDesc,
  'timestamp:asc': SearchSort.TimestampAsc,
  'timestamp:desc': SearchSort.TimestampDesc,
}

watch(sort, (value) => {
  if (!value) return
  const next = SORT_MAP[`${value.field}:${value.direction}`]
  if (next) sortBy.value = next
})

const variables = computed(() => ({
  sortBy: sortBy.value,
}))

const { result, loading, fetchMore } = useArchivedFilesQuery(variables, {
  fetchPolicy: 'cache-and-network',
})

const items = computed(
  () => result.value?.archivedFiles.items as ArchivedFile[] | undefined
)

const selectedItem = computed(() =>
  items.value?.find((item) => item.id === selected.value[0])
)

const fetchNextPage = () => {
  if (!result.value?.archivedFiles.next || loading.value) return

  fetchMore({
    variables: {
      next: result.value.archivedFiles.next,
    },
    updateQuery: (previousData, { fetchMoreResult: newData }) => {
      if (!newData) return previousData

      return {
        __typename: 'Query',
        archivedFiles: {
          ...previousData.archivedFiles,
          items: [
            ...previousData.archivedFiles.items,
            ...newData.archivedFiles.items,
          ],
          next: newData.archivedFiles.next,
        },
      }
    },
  })
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const tableEl = tableRef.value?.$el
  if (tableEl && !tableEl.contains(target)) {
    selected.value = []
  }
}

onBeforeUnmount(() => {
  selected.value = []
})
</script>
